import { Account, AccountUser, Club, Night } from "@mixitone/models";
import { ClubPlayer, type Game, type GameSet, type Group, Note, type Player } from "@mixitone/models";
import { List } from "@mixitone/oom";
import { isNil, isPresent, withStateToggle } from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import MixitoneController from "controllers/MixitoneController";
import { messagesService } from "services";
import agentToolsService from "services/AgentToolsService";

interface State {
  night: Night;
  player?: Player;
  club: Club;
  players: List<Player>;
  clubPlayer: ClubPlayer;
  clubPlayers: List<ClubPlayer>;
  clubPlayersInSession: List<ClubPlayer>;
  pairedPlayer: Player | null;
  setGames: { gameSet: GameSet; game: Game; players: Player[] }[];
  loading: boolean;
  toggling: boolean;
  updating: Set<"group" | "notes">;
  errors: { name?: string };
  notes: List<Note>;
  newNote: Note;
  addingNote: boolean;
}

interface Props {
  token: string;
  clubId: string;
  id: string;
}

export class PlayerDrawerController extends MixitoneController<State, Props> {
  override get initialState() {
    return {
      setGames: [],
      loading: true,
      toggling: false,
      updating: new Set<"group" | "notes">(),
      players: new List<Player>(),
      clubPlayer: new ClubPlayer({}),
      clubPlayers: new List<ClubPlayer>(),
      clubPlayersInSession: new List<ClubPlayer>(),
      pairedPlayer: null,
      errors: {},
      night: new Night({}),
      club: new Club({}),
      notes: new List<Note>(),
      newNote: new Note({}),
      addingNote: false,
    };
  }

  override async initialize(props: Props) {
    this.registerAgentResources();
    await this.changeProps(props);
  }

  @withStateToggle("loading")
  async changeProps({ token, clubId, id }: Props) {
    if (this.state.clubPlayer?.id === id) return;

    this.state.toggling = false;
    this.state.updating.clear();

    // Initialize notes list to empty to avoid undefined
    this.state.notes = new List();

    const [night, club] = await Promise.all([
      Night.query().preload("players", "game_sets").find(token, { noCache: true }),
      Club.query().preload("club_players", "groups").find(clubId, { noCache: true }),
    ]);
    this.state.night = night;
    this.state.club = club;
    this.state.club.club_players.subscribe({ update: true, insert: true });
    this.state.clubPlayers = new List(...this.state.club.club_players);

    this.state.clubPlayers.forEach((cp) => {
      const group = this.state.club.groups.findById(cp.group_id!);
      if (!group) {
        console.error("Group not found for club player");
        captureException(new Error("Group not found for club player when loading drawer"), {
          extra: {
            clubPlayerId: cp.id,
            groupId: cp.group_id,
          },
        });
        return;
      }
      cp.group.set(group);
    });

    const clubPlayer = this.state.clubPlayers.findById(id);
    if (!clubPlayer) {
      captureException(new Error("Club player not found when loading drawer"));
      console.error("Club player not found when loading drawer");

      // Find the player in the session that references this deleted club player
      const orphanedPlayer = this.state.night.players.find((p) => p.club_player_id === id);

      if (orphanedPlayer && this.state.night.active) {
        // Remove the player from the session
        this.state.night.players.removeById(orphanedPlayer.uniqueId);

        // Delete the player record if we have permission
        if (this.state.club.hasSessionPermission()) {
          await orphanedPlayer.destroy();
        }

        messagesService.addMessage("Player has been removed from the session.", "error");
      } else {
        messagesService.addMessage("Player not found", "error");
      }

      // Close the drawer
      this.actionCloseModal();
      return;
    }

    this.state.clubPlayer = clubPlayer;

    // Load notes directly here to ensure they're loaded before the component renders
    await clubPlayer.notes.load();
    this.state.notes = new List(...clubPlayer.notes);

    // Add subscription to notes for real-time updates
    clubPlayer.notes.subscribe({ update: true, insert: true });

    await this.loadPlayers();
    await this.loadPlayer();

    this.state.clubPlayersInSession = new List(
      ...this.state.clubPlayers.filter(
        (cp) =>
          cp.id !== this.state.clubPlayer.id && this.state.players.some((p) => p.club_player_id === cp.id),
      ),
    );
  }

  get group() {
    return this.state.clubPlayer.group;
  }

  get pairedClubPlayer() {
    return this.state.clubPlayers.find((cp) => cp.id === this.state.pairedPlayer?.club_player_id);
  }

  async loadPlayers() {
    const players = this.state.night.players;
    players.forEach((player) => {
      const playerClubPlayer = this.state.clubPlayers.findById(player.club_player_id!);
      if (playerClubPlayer) {
        player.clubPlayer.set(playerClubPlayer);
      }
    });
    this.state.players = new List(...players.filter((p) => isPresent(p.clubPlayer)));
  }

  async loadPlayer() {
    const player = this.state.players.find((p) => p.club_player_id === this.state.clubPlayer.id);
    this.state.player = player;
    if (!player) return;

    const clubPlayer = this.state.clubPlayer;

    if (isNil(clubPlayer)) {
      console.error("Player does not have a club player");
      captureException(new Error("Player does not have a club player when loading drawer"));
      return;
    }

    // Initialize notes list before loading to avoid undefined
    this.state.notes = new List();

    // Load notes and ensure they're properly initialized
    await this.state.clubPlayer.notes.load();

    // Make sure to create a new List to trigger reactivity
    this.state.notes = new List(...this.state.clubPlayer.notes);

    // Add subscription to notes for real-time updates
    this.state.clubPlayer.notes.subscribe({ update: true, insert: true });

    // Initialize new note template
    this.state.newNote = new Note({
      account_user_id: AccountUser.current?.id,
      club_player_id: this.state.clubPlayer.id,
      night_id: this.state.night.id,
      note: "", // Ensure note field is initialized
    });

    if (player.partner_id) {
      this.state.pairedPlayer = this.state.players.find((p) => p.id === player.partner_id) || null;
    } else {
      this.state.pairedPlayer = null;
    }

    this.state.setGames = this.state.night.game_sets
      .filter((gs) => gs.started)
      .reduce(
        (acc, gs) => {
          const game = gs.games.find((g) => g.hasPlayer(player.id!));
          if (!game) return acc;

          acc.push({
            gameSet: gs,
            game,
            players: game.playerIds
              .reduce((acc, id) => {
                if (!id) return acc;
                const player = this.state.players.findById(id);
                if (!player) return acc;
                if (player.id === this.state.player?.id) return acc;
                acc.push(player);
                return acc;
              }, [] as Player[])
              .toSorted((a, b) => a.name!.localeCompare(b.name!)),
          });

          return acc;
        },
        [] as State["setGames"],
      );
  }

  async actionMoveToGroup(group: Group) {
    if (!this.state.clubPlayer) return;
    this.state.clubPlayer.group = group;
    await this.state.clubPlayer.save();
  }

  async actionUpdatePlayerGraduationLevel(level: number) {
    if (!Account.current?.hasFeature("group_levels")) return;
    if (!this.state.clubPlayer) return;
    this.state.clubPlayer.graduation_level = level;
    await this.state.clubPlayer.save();
  }

  async actionSave() {
    if (!this.state.clubPlayer) return;
    if (!this.state.player) return;

    await this.state.clubPlayer.save();
    this.state.player.name = this.state.clubPlayer.name;
    await this.state.player.save();
  }

  actionOpenMemberDrawer() {
    this.actionOpenModal("/clubs/:clubId/players/:id/edit", {
      params: {
        clubId: this.state.club.id!,
        id: this.state.clubPlayer.id!,
      },
    });
  }

  async actionSetPairedPlayer(clubPlayer: ClubPlayer | null) {
    const player = this.state.players.find((p) => p.club_player_id === clubPlayer?.id) || null;
    this.state.pairedPlayer = player;

    if (player) {
      await this.state.night.pairPlayers(this.state.player!, player);
    } else {
      await this.state.night.unpairPlayer(this.state.player!);
    }
  }

  @withStateToggle("toggling")
  async actionJoin() {
    await this.state.night.togglePlayer(this.state.clubPlayer);
    await this.loadPlayers();
    await this.loadPlayer();
  }

  actionToggleAddingNote(adding: boolean) {
    this.state.addingNote = adding;

    if (adding) {
      // Reset the new note template
      this.state.newNote = new Note({
        account_user_id: AccountUser.current?.id,
        club_player_id: this.state.clubPlayer.id,
        night_id: this.state.night.id,
        note: "",
      });
    }
  }

  async actionAddNote() {
    if (!this.state.newNote || !this.state.newNote.note) return;

    this.state.updating.add("notes");

    try {
      // Ensure required fields are set
      this.state.newNote.club_player_id = this.state.clubPlayer.id;
      this.state.newNote.night_id = this.state.night.id;
      this.state.newNote.account_user_id = AccountUser.current?.id;

      await this.state.newNote.save();
      await this.reloadNotes();

      // Reset the new note template
      this.state.newNote = new Note({
        account_user_id: AccountUser.current?.id,
        club_player_id: this.state.clubPlayer.id,
        night_id: this.state.night.id,
        note: "",
      });

      this.state.addingNote = false;
    } finally {
      this.state.updating.delete("notes");
    }
  }

  async actionToggleNoteResolved(note: Note) {
    if (!note.persisted) return;

    this.state.updating.add("notes");

    try {
      if (note.resolved_at) {
        note.resolved_at = undefined;
      } else {
        note.resolved_at = new Date();
      }

      await note.save();
      await this.reloadNotes();
    } finally {
      this.state.updating.delete("notes");
    }
  }

  async actionDeleteNote(note: Note) {
    if (!note.persisted) return;

    this.state.updating.add("notes");

    try {
      await note.destroy();
      await this.reloadNotes();
    } finally {
      this.state.updating.delete("notes");
    }
  }

  @withStateToggle("toggling")
  async actionRetire() {
    if (!this.state.player) return;
    await this.state.night.togglePlayer(this.state.clubPlayer);
    await this.loadPlayers();
    this.state.player = undefined;
  }

  @withStateToggle("loading")
  async actionSwitchDrawer(id: string) {
    const cp = this.state.clubPlayers.findById(id);
    if (!cp) return;
    this.state.clubPlayer = cp;
    await this.loadPlayer();
  }

  // Helper method to ensure notes are properly loaded and updated
  async reloadNotes() {
    await this.state.clubPlayer.notes.reload();
    this.state.notes = new List(...this.state.clubPlayer.notes);
  }

  private registerAgentResources() {
    this.addDependency(
      agentToolsService.addResources(this, {
        context: () =>
          `The user is viewing a player drawer for
          player ${this.state.clubPlayer.name} with id ${this.state.player?.id}
          and club player id ${this.state.clubPlayer.id}
          If they refer to "this" right now they mean this drawer.
          The player has notes ${this.state.notes.join(" ")}}
          and is in group ${this.state.clubPlayer.group?.id}.
          The player has played in the following games:
          ${this.state.setGames
            .map(
              ({ gameSet, game, players }) =>
                `Game set ${gameSet.index} on court ${game.index! + 1} 
                 with ${players.map((p) => p.id).join(", ")}`,
            )
            .join("\n")}
          `,
      }),
    );
  }
}
